/**
 * BusinessSearchSection Component
 * Inline business search that collapses to a card after selection
 * Includes inline suggest form with fuzzy duplicate detection
 */

import { mapBusinessWireToDomain } from '@/api/mappers/business';
import { businessesNearbyQueryOptions, businessesSearchInfiniteQueryOptions } from '@/api/queries/businesses';
import { SearchBar } from '@/components/forms/search-bar';
import { TextInput } from '@/components/ui/text-input';
import { toast } from '@/components/ui/toast';
import { neutral } from '@/constants/theme';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useUnifiedColorScheme } from '@/hooks/use-unified-color-scheme';
import { useUserLocation } from '@/hooks/use-user-location';
import { useAppTheme } from '@/providers/theme-provider';
import { useUnits } from '@/providers/units-provider';
import { Business } from '@/types/business';
import { formatDistance } from '@/utils/distance';
import { findSimilarItems } from '@/utils/fuzzy-match';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  ScrollView,
  Text,
  View,
} from 'react-native';
import { useSuggestBusiness } from '../hooks/use-suggest-business';

const CUISINE_TYPES = [
  'american', 'italian', 'mexican', 'chinese', 'japanese', 'thai', 'indian',
  'french', 'mediterranean', 'korean', 'vietnamese', 'greek', 'spanish',
  'middle_eastern', 'bbq', 'seafood', 'steakhouse', 'pizza', 'fast_food',
  'cafe', 'bakery', 'desserts', 'vegan', 'vegetarian', 'other',
];

export interface BusinessSearchSectionProps {
  /** Currently selected business */
  selectedBusiness: Business | null;
  /** Callback when a business is selected */
  onSelect: (business: Business) => void;
  /** Callback when selection is cleared */
  onClear: () => void;
  /**
   * When true, the results list expands to fill all available vertical space
   * instead of capping at a fixed max height. Use when the component is the
   * only content in a flex-1 container (i.e. no business selected yet).
   */
  fillVertical?: boolean;
}

export function BusinessSearchSection({
  selectedBusiness,
  onSelect,
  onClear,
  fillVertical = false,
}: BusinessSearchSectionProps) {
  const theme = useAppTheme();
  const colorScheme = useUnifiedColorScheme();
  const backgroundColor = colorScheme === 'dark' ? neutral[950] : neutral[50];
  const borderColor = colorScheme === 'dark' ? '#2A2826' : neutral[200];
  const primaryColor = colorScheme === 'dark' ? '#9ACD32' : '#3daa00';
  const units = useUnits();
  const [searchQuery, setSearchQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(!selectedBusiness);

  // Suggest form state
  const [isAddingBusiness, setIsAddingBusiness] = useState(false);
  const [newBusinessName, setNewBusinessName] = useState('');
  const [newBusinessAddress, setNewBusinessAddress] = useState('');
  const [newBusinessCuisine, setNewBusinessCuisine] = useState('');
  const [showSimilarWarning, setShowSimilarWarning] = useState(false);

  const suggestMutation = useSuggestBusiness();

  // Collapse when a business is set externally (e.g., pre-populated from route params)
  useEffect(() => {
    if (selectedBusiness) {
      setIsExpanded(false);
    }
  }, [selectedBusiness]);

  // Get user location
  const { coords, defaultLocation } = useUserLocation();

  // Debounce search query
  const debouncedQuery = useDebouncedValue(searchQuery, 300);

  // Nearby businesses query (include pending)
  const nearbyLat = coords?.latitude ?? defaultLocation.latitude;
  const nearbyLng = coords?.longitude ?? defaultLocation.longitude;

  const { data: nearbyData, isLoading: isNearbyLoading } = useQuery({
    ...businessesNearbyQueryOptions({
      latitude: nearbyLat,
      longitude: nearbyLng,
      radius_km: 5,
      limit: 20,
      status: 'active,pending',
    }),
    enabled: isExpanded && nearbyLat !== 0 && nearbyLng !== 0 && !debouncedQuery.trim(),
  });

  // Search businesses query (include pending) — infinite for seamless pagination
  const {
    data: searchData,
    isLoading: isSearchLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery(
    businessesSearchInfiniteQueryOptions({
      query: debouncedQuery.trim(),
      status: 'active,pending',
      per_page: 15,
      latitude: nearbyLat,
      longitude: nearbyLng,
      sort_by: 'relevance',
    })
  );

  // Map nearby businesses
  const nearbyBusinesses = useMemo(() => {
    if (!nearbyData) return [];
    return nearbyData.items.map((item) => {
      const business = mapBusinessWireToDomain(item);
      const distanceText = formatDistance(item.distance_km, units);
      return { ...business, distance: distanceText };
    });
  }, [nearbyData, units]);

  // Map search results — flatten across all loaded pages
  const mappedSearchResults = useMemo(() => {
    if (!searchData) return [];
    return searchData.pages.flatMap((page) => page.items).map(mapBusinessWireToDomain);
  }, [searchData]);

  // Determine which list to show
  const isSearching = debouncedQuery.trim().length >= 2;
  const displayBusinesses = isSearching ? mappedSearchResults : nearbyBusinesses;
  const isLoading = isSearching ? isSearchLoading : isNearbyLoading;

  // Fuzzy match against all displayed businesses for suggest warning
  const similarBusinesses = useMemo(
    () => findSimilarItems(newBusinessName, displayBusinesses, (b) => b.name),
    [newBusinessName, displayBusinesses]
  );

  const resetSuggestForm = useCallback(() => {
    setNewBusinessName('');
    setNewBusinessAddress('');
    setNewBusinessCuisine('');
    setShowSimilarWarning(false);
  }, []);

  const handleSelect = useCallback(
    (business: Business) => {
      onSelect(business);
      setIsExpanded(false);
      setSearchQuery('');
      setIsAddingBusiness(false);
      resetSuggestForm();
    },
    [onSelect, resetSuggestForm]
  );

  const handleChange = useCallback(() => {
    setIsExpanded(true);
  }, []);

  const handleStartSuggest = useCallback(() => {
    setIsAddingBusiness(true);
    setNewBusinessName(searchQuery.trim());
    setNewBusinessAddress('');
    setNewBusinessCuisine('');
    setShowSimilarWarning(false);
  }, [searchQuery]);

  const handleCancelSuggest = useCallback(() => {
    setIsAddingBusiness(false);
    resetSuggestForm();
  }, [resetSuggestForm]);

  const handleNameChange = useCallback((text: string) => {
    setNewBusinessName(text);
    setShowSimilarWarning(false);
  }, []);

  const handleSuggestSubmit = useCallback(async () => {
    if (!newBusinessName.trim() || !newBusinessAddress.trim()) return;

    // On first submit attempt, show similar items warning if any exist
    if (!showSimilarWarning && similarBusinesses.length > 0) {
      setShowSimilarWarning(true);
      return;
    }

    try {
      const created = await suggestMutation.mutateAsync({
        name: newBusinessName.trim(),
        address: newBusinessAddress.trim(),
        cuisine_type: newBusinessCuisine || undefined,
      });
      toast.success('Restaurant suggested!');
      handleSelect(created);
    } catch {
      toast.error('Failed to suggest restaurant.');
    }
  }, [
    newBusinessName,
    newBusinessAddress,
    newBusinessCuisine,
    showSimilarWarning,
    similarBusinesses.length,
    suggestMutation,
    handleSelect,
  ]);


  // Collapsed card view
  if (selectedBusiness && !isExpanded) {
    return (
      <View className="mb-4">
        <Text
          className="text-xl text-foreground mb-2"
          accessibilityRole="header"
        >
          Restaurant
        </Text>
        <View className="p-4 rounded-lg flex-row items-center justify-between bg-surface border border-border">
          <View className="flex-1 mr-3">
            <View className="flex-row items-center">
              <Text className="text-base font-semibold text-foreground">
                {selectedBusiness.name}
              </Text>
            </View>
            {selectedBusiness.cuisineType && (
              <Text className="text-sm mt-1 text-muted-foreground">
                {selectedBusiness.cuisineType}
              </Text>
            )}
          </View>
          <Pressable
            onPress={handleChange}
            className="px-3 py-1.5 rounded-full bg-primary/[0.09]"
            accessibilityRole="button"
            accessibilityLabel="Change restaurant"
          >
            <Text className="text-sm font-medium text-primary">
              Change
            </Text>
          </Pressable>
        </View>
      </View>
    );
  }

  // Expanded search view
  return (
    <View style={fillVertical ? { flex: 1 } : { marginBottom: 16 }}>
      <Text
        className="text-sm font-medium mb-2 text-muted-foreground"
        accessibilityRole="header"
      >
        Where did you eat?
      </Text>

      {/* Search Bar */}
      {!isAddingBusiness && (
        <SearchBar
          value={searchQuery}
          onChangeText={setSearchQuery}
          onClear={() => setSearchQuery('')}
          placeholder="Search restaurants..."
        />
      )}

      {/* Inline Suggest Form */}
      {isAddingBusiness ? (
        <View className="p-4 rounded-lg bg-surface border border-border">
          <Text
            className="text-sm font-semibold mb-3 text-foreground"
            accessibilityRole="header"
          >
            Suggest a new restaurant
          </Text>

          {/* Name (required) */}
          <TextInput
            value={newBusinessName}
            onChangeText={handleNameChange}
            placeholder="Restaurant name"
            autoFocus
            inputContainerStyle={{
              backgroundColor,
              marginBottom: 8,
            }}
          />

          {/* Address (required) */}
          <TextInput
            value={newBusinessAddress}
            onChangeText={setNewBusinessAddress}
            placeholder="Address"
            inputContainerStyle={{
              backgroundColor,
              marginBottom: 8,
            }}
          />

          {/* Cuisine type (optional) */}
          <Text
            className="text-xs font-medium mb-2 text-muted-foreground"
            accessibilityRole="header"
          >
            Cuisine type (optional)
          </Text>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={{ marginBottom: 12 }}
          >
            <View style={{ flexDirection: 'row', gap: 6 }}>
              {CUISINE_TYPES.map((type) => {
                const isSelected = newBusinessCuisine === type;
                return (
                  <Pressable
                    key={type}
                    onPress={() => setNewBusinessCuisine(isSelected ? '' : type)}
                    accessibilityRole="button"
                    accessibilityLabel={type.replace(/_/g, ' ')}
                    accessibilityState={{ selected: isSelected }}
                    style={{
                      paddingHorizontal: 10,
                      paddingVertical: 6,
                      borderRadius: 16,
                      minHeight: 44,
                      backgroundColor: isSelected ? primaryColor : backgroundColor,
                      borderWidth: 1,
                      borderColor: isSelected ? primaryColor : borderColor,
                    }}
                  >
                    <Text
                      className={`text-xs ${isSelected ? '' : 'text-foreground'}`}
                      style={isSelected ? { color: '#fff' } : undefined}
                    >
                      {type.replace(/_/g, ' ')}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </ScrollView>

          {/* Similar businesses warning */}
          {showSimilarWarning && similarBusinesses.length > 0 && (
            <View className="bg-warning/[0.06] border border-warning/[0.19] rounded-lg p-3 mb-3">
              <Text className="text-sm font-medium mb-2 text-warning">
                Did you mean one of these?
              </Text>
              {similarBusinesses.slice(0, 3).map((biz) => (
                <Pressable
                  key={biz.id}
                  onPress={() => handleSelect(biz)}
                  className="py-2 px-3 rounded mb-1 bg-background"
                  accessibilityRole="button"
                  accessibilityLabel={`Select ${biz.name}`}
                >
                  <View className="flex-row items-center">
                    <Text className="text-sm font-medium text-foreground">
                      {biz.name}
                    </Text>
                    {biz.city && (
                      <Text className="text-xs ml-2 text-muted-foreground">
                        {biz.city}
                      </Text>
                    )}
                  </View>
                </Pressable>
              ))}
              <Pressable
                onPress={handleSuggestSubmit}
                className="mt-2"
              >
                <Text className="text-xs text-primary">
                  No, add &quot;{newBusinessName.trim()}&quot; as a new restaurant
                </Text>
              </Pressable>
            </View>
          )}

          {/* Form buttons */}
          <View className="flex-row" style={{ gap: 8 }}>
            <Pressable
              onPress={handleCancelSuggest}
              className="flex-1 py-2.5 rounded-lg items-center justify-center bg-background border border-border"
              style={{ minHeight: 44 }}
              accessibilityRole="button"
            >
              <Text className="text-sm font-medium text-muted-foreground">
                Cancel
              </Text>
            </Pressable>
            <Pressable
              onPress={handleSuggestSubmit}
              disabled={!newBusinessName.trim() || !newBusinessAddress.trim() || suggestMutation.isPending}
              className={`flex-1 py-2.5 rounded-lg items-center justify-center ${(!newBusinessName.trim() || !newBusinessAddress.trim()) ? 'bg-border' : 'bg-primary'}`}
              style={{ minHeight: 44 }}
              accessibilityRole="button"
            >
              {suggestMutation.isPending ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text className="text-sm font-medium text-white">
                  Suggest Restaurant
                </Text>
              )}
            </Pressable>
          </View>
        </View>
      ) : (
        <>
          {/* Section Label */}
          <Text className="text-xs font-medium mt-3 mb-2 text-muted-foreground">
            {isSearching ? 'SEARCH RESULTS' : 'NEARBY'}
          </Text>

          {/* Business List */}
          {isLoading ? (
            <View className="py-6 items-center">
              <ActivityIndicator size="small" color={theme.colors.primary} />
            </View>
          ) : displayBusinesses.length > 0 ? (
            <>
              <View style={fillVertical ? { flex: 1 } : { maxHeight: 300 }}>
                <FlatList
                  data={displayBusinesses}
                  keyExtractor={(item) => item.id}
                  scrollEnabled={true}
                  nestedScrollEnabled={true}
                  onEndReached={isSearching && hasNextPage ? () => fetchNextPage() : undefined}
                  onEndReachedThreshold={0.3}
                  ListFooterComponent={
                    isFetchingNextPage ? (
                      <View className="py-3 items-center">
                        <ActivityIndicator size="small" color={theme.colors.primary} />
                      </View>
                    ) : null
                  }
                  renderItem={({ item }) => (
                    <Pressable
                      onPress={() => handleSelect(item)}
                      className="p-3 rounded-lg mb-2 bg-surface border border-border"
                      accessibilityRole="button"
                      accessibilityLabel={`Select ${item.name}`}
                    >
                      <View className="flex-row items-center justify-between">
                        <View className="flex-1">
                          <View className="flex-row items-center">
                            <Text className="font-medium text-foreground">
                              {item.name}
                            </Text>
                          </View>
                          <View className="flex-row items-center mt-1">
                            {item.cuisineType && (
                              <Text className="text-xs mr-2 text-muted-foreground">
                                {item.cuisineType}
                              </Text>
                            )}
                            {!isSearching && (item as any).distance && (
                              <Text className="text-xs text-muted-foreground">
                                {(item as any).distance}
                              </Text>
                            )}
                          </View>
                        </View>
                        <Ionicons
                          name="chevron-forward"
                          size={18}
                          className="text-muted-foreground"
                        />
                      </View>
                    </Pressable>
                  )}
                />
              </View>
              {/* Suggest link below results */}
              <Pressable
                onPress={handleStartSuggest}
                className="py-3 items-center"
                accessibilityRole="link"
                accessibilityLabel="Suggest a new restaurant"
              >
                <Text className="text-sm text-primary">
                  Don&apos;t see your restaurant? Add it
                </Text>
              </Pressable>
            </>
          ) : (
            <View className="py-6 items-center">
              <Ionicons
                name={isSearching ? 'search-outline' : 'location-outline'}
                size={32}
                className="text-muted-foreground"
              />
              <Text className="text-sm mt-2 text-center text-muted-foreground">
                {isSearching ? 'No restaurants found' : 'No nearby restaurants'}
              </Text>
              <Pressable
                onPress={handleStartSuggest}
                className="mt-2"
                accessibilityRole="link"
                accessibilityLabel="Suggest a new restaurant"
              >
                <Text className="text-sm font-medium text-primary">
                  {isSearching ? 'Suggest a new restaurant' : 'Add a restaurant'}
                </Text>
              </Pressable>
            </View>
          )}
        </>
      )}
    </View>
  );
}

export default BusinessSearchSection;
